'use client'
import { FC } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import { generatePath } from '@utils/path.utils'
import { SimpleStat } from '@components/shared/simple-stat/SimpleStat'
import {
  Fund,
  FundStage,
  isOpenPreIpoFundStage,
  resolvePreIpoFundStage,
} from '@domain/funds/model'
import { createStrapiMediaUrl } from '@utils/strapi.utils'
import { Button } from '@components/ui/button'
import { Tooltip } from '@components/ui/tooltip'
import { formatNumberWithShortcuts } from '@utils'
import {
  COMPANY_VALUATION_LABEL,
  MARKETPLACE_ENTRY_LABEL,
  WLTH_ENTRY_LABEL,
} from '@domain/pre-ipo-access/prototypeSharedCopy'
import type { PreIpoFundCardMetadata } from '@domain/landing/homePageFundPayload'
import type { StrapiComponent, StrapiFundData } from '@types'
import { paths } from '@router/paths'
import { FundCardWithSections } from '../fund-card/FundCardWithSections'
import { shouldShowAdjustedMarketCapInfo } from '@domain/funds/utils/adjustedMarketCap'
import { AdjustedMarketCapTooltipContent } from '@domain/funds/components/AdjustedMarketCapTooltipContent'
import { getMarketplaceCollectionHref } from '@domain/portfolio/getHoldingBuyMoreHref'

const StageInvestButton = dynamic(
  () => import('../invest-button').then((mod) => mod.StageInvestButton),
  { ssr: false },
)

export interface PreIpoFundListItemProps {
  fund: Fund
  fundMetadata: PreIpoFundCardMetadata | StrapiComponent<StrapiFundData>
  variant?: 'light' | 'dark'
  lowestInAppValuation?: number | null
}

export const PreIpoFundListItem: FC<PreIpoFundListItemProps> = ({
  fund,
  fundMetadata,
  variant = 'light',
  lowestInAppValuation,
}) => {
  const {
    fundUrlId: fundId,
    fundDescription,
    fundCoverImage,
    stage,
    referenceValue,
  } = fundMetadata
  const referenceValuation = referenceValue ?? 0
  const normalizedStage = stage.toLowerCase()
  const isPaused = normalizedStage === FundStage.Paused.toLowerCase()

  const detailsPath = generatePath(paths.funds.exclusiveAccess.preIPOAccessDetails, {
    fundId,
  })
  const marketplaceHref = getMarketplaceCollectionHref(
    { investmentNft: fund?.details?.investmentNft ?? '' },
    fundMetadata,
  )

  const fundStage = resolvePreIpoFundStage(stage)
  const isOpenFund = isOpenPreIpoFundStage(stage)
  const isNewStage = stage?.toLowerCase() === FundStage.New.toLowerCase()

  const currentValue = fundMetadata.communityFundCustomData?.currentValuation
  const displayInAppValuation = isOpenFund
    ? Number(referenceValue ?? 0)
    : Number(lowestInAppValuation ?? currentValue ?? 0)
  const leftValuationLabel = isOpenFund ? WLTH_ENTRY_LABEL : MARKETPLACE_ENTRY_LABEL
  const adjustedMarketCapTooltip = shouldShowAdjustedMarketCapInfo(fundId) ? (
    <AdjustedMarketCapTooltipContent />
  ) : undefined

  return (
    <FundCardWithSections
      title={fundMetadata.fundName}
      description={fundDescription}
      stage={fundStage}
      variant={variant}
      detailsPath={detailsPath}
      proofLink={fundMetadata.proofLink}
      imgIcon={createStrapiMediaUrl(fundCoverImage)}
      content={
        <>
          {isOpenFund ? (
            <div className="flex flex-col gap-3 w-full">
              <SimpleStat
                valueClassName={variant === 'dark' ? 'text-white' : ''}
                labelClassName={`uppercase ${variant === 'dark' ? 'text-white' : ''}`}
                prefixClassName={variant === 'dark' ? 'text-white' : ''}
                label={leftValuationLabel}
                value={formatNumberWithShortcuts(displayInAppValuation)}
                prefix="$"
              />
              <SimpleStat
                valueClassName={variant === 'dark' ? 'text-white' : ''}
                labelClassName={`uppercase ${variant === 'dark' ? 'text-white' : ''}`}
                prefixClassName={variant === 'dark' ? 'text-white' : ''}
                label={COMPANY_VALUATION_LABEL}
                tagVariant="filled"
                value={formatNumberWithShortcuts(currentValue || 0)}
                prefix="$"
                percentageChangeClassName="text-xs xs:text-sm"
                percentageChange={Number(
                  (
                    ((Number(currentValue || 0) - Number(referenceValuation || 0)) /
                      Number(referenceValuation || 1)) *
                    100
                  ).toFixed(2),
                )}
                tooltip={adjustedMarketCapTooltip}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 w-full">
              <SimpleStat
                valueClassName={variant === 'dark' ? 'text-white' : ''}
                labelClassName={`uppercase ${variant === 'dark' ? 'text-white' : ''}`}
                prefixClassName={variant === 'dark' ? 'text-white' : ''}
                label={leftValuationLabel}
                value={formatNumberWithShortcuts(displayInAppValuation)}
                prefix="$"
              />
              <SimpleStat
                valueClassName={variant === 'dark' ? 'text-white' : ''}
                labelClassName={`uppercase ${variant === 'dark' ? 'text-white' : ''}`}
                prefixClassName={variant === 'dark' ? 'text-white' : ''}
                label={COMPANY_VALUATION_LABEL}
                tagVariant="filled"
                value={formatNumberWithShortcuts(currentValue || 0)}
                prefix="$"
                percentageChangeClassName="text-xs xs:text-sm"
                percentageChange={Number(
                  (
                    ((Number(currentValue || 0) - Number(referenceValuation || 0)) /
                      Number(referenceValuation || 1)) *
                    100
                  ).toFixed(2),
                )}
                tooltip={adjustedMarketCapTooltip}
              />
            </div>
          )}
        </>
      }
      footerButtons={
        isNewStage ? (
          <div className="flex w-full flex-row items-center gap-3">
            <Link
              href={detailsPath}
              className="min-w-0 flex-1"
              aria-label={`View details for ${fundMetadata.fundName}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant={variant === 'dark' ? 'secondaryWhite' : 'secondary'}
                size="md"
                className="w-full"
              >
                More info
              </Button>
            </Link>
            <StageInvestButton
              stage={stage ?? ''}
              type="fund"
              className="!w-auto shrink-0 whitespace-nowrap"
              fundAddress={fundMetadata.fundAddress}
              fundState={fund?.details?.state}
              fundUrlId={fundMetadata.fundUrlId}
              isPreIpo
            />
          </div>
        ) : (
          <>
            <Link
              href={detailsPath}
              className="w-full sm:w-1/2"
              aria-label={`View details for ${fundMetadata.fundName}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant={variant === 'dark' ? 'secondaryWhite' : 'secondary'}
                size="md"
                className="w-full"
              >
                More info
              </Button>
            </Link>
            {isOpenFund ? (
              <StageInvestButton
                stage={stage ?? ''}
                type="fund"
                className="w-full sm:w-1/2"
                fundAddress={fundMetadata.fundAddress}
                fundState={fund?.details?.state}
                fundUrlId={fundMetadata.fundUrlId}
                isPreIpo
              />
            ) : (
              <Tooltip
                content={isPaused ? 'Temporarily unavailable' : ''}
                side="top"
                triggerProps={{ asChild: true }}
              >
                <span className="w-full sm:w-1/2">
                  {marketplaceHref && !isPaused ? (
                    <Link href={marketplaceHref} onClick={(e) => e.stopPropagation()}>
                      <Button variant="primary" size="md" className="w-full">
                        Marketplace
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="primary" size="md" className="w-full" disabled>
                      Marketplace
                    </Button>
                  )}
                </span>
              </Tooltip>
            )}
          </>
        )
      }
    />
  )
}
